import React, { useState } from "react";
import { Plus, Edit, Trash2, CheckCircle2, AlertCircle, X, AlertTriangle } from "lucide-react";
import { SiStripe, SiQuickbooks } from "react-icons/si";
import { CreditCard, Database } from "lucide-react";
import ChargebeeIcon from "../../../assets/images/Chargebee_ideEpxhfdE_1.svg";

/** Issue shown in the error-column modal; stepId is used to open that step when clicked. */
export interface ConnectionIssue {
  id: string;
  label: string;
  stepId: number;
  sourceType?: string;
}

export interface RevenueConnection {
  id: string;
  type: string;
  name: string;
  lastSync?: string;
  sourceEnvironment?: string;
  targetEnvironment?: string;
  accountIdLabel?: string;
  accountIdValue?: string;
  syncStartDate?: string;
  accountIdentifierKey?: string;
  sourceConfig?: Record<string, unknown>;
  status?: "active" | "incomplete";
  hasError?: boolean;
  /** Whether this connection is fully complete (configured + all steps done). */
  isComplete?: boolean;
  /** Number of issues (incomplete steps + setup health). */
  issueCount?: number;
  /** Issues to show in the error-column modal (incomplete steps + setup health). */
  issues?: ConnectionIssue[];
  /** Category stored from user's selection (e.g. 'subscription' | 'transactions'). */
  sourceCategory?: string;
}

interface RevenueConnectionsListProps {
  connections: RevenueConnection[];
  onAddNew: () => void;
  onEdit: (connection: RevenueConnection) => void;
  onDelete: (connection: RevenueConnection) => void;
  canAddNew?: boolean;
  addNewError?: string | null;
  canDelete?: (connection: RevenueConnection) => boolean;
  onOpenSetupHealthStatus?: () => void;
  /** When user clicks an issue in the modal, open that step (and optionally select this source for step 2). */
  onOpenStepForIssue?: (connection: RevenueConnection, issue: ConnectionIssue) => void;
  /** Optional content to show on the left of the "Connect New Source" row (e.g. environment selector with "Select Environment" heading). */
  leftContent?: React.ReactNode;
}

const formatEnvName = (env: string) =>
  env ? env.charAt(0).toUpperCase() + env.slice(1).toLowerCase() : "—";

/** Format ISO date string as "23 May 2026, 14:30" (24-hour). */
function formatLastSyncDate(isoDate: string | undefined): string {
  if (!isoDate || typeof isoDate !== "string") return "—";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.getDate();
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const h = d.getHours().toString().padStart(2, "0");
  const m = d.getMinutes().toString().padStart(2, "0");
  return `${day} ${month} ${year}, ${h}:${m}`;
}

function getConnectionCategory(type: string, sourceCategory?: string): { label: string; style: string } {
  const category = sourceCategory || (() => {
    switch (type) {
      case "stripe": case "chargebee": return "subscription";
      case "api": case "csv": case "salesforce": return "transactions";
      default: return "";
    }
  })();
  switch (category) {
    case "subscription":
      return { label: "Subscription", style: "bg-blue-50 text-blue-700" };
    case "transactions":
      return { label: "Transactions & invoices", style: "bg-purple-50 text-purple-700" };
    default:
      return { label: "—", style: "text-gray-500" };
  }
}

function getSourceIcon(type: string) {
  switch (type) {
    case "stripe":
      return <SiStripe className="w-5 h-5" style={{ color: "#635BFF" }} />;
    case "chargebee":
      return (
        <img src={ChargebeeIcon} alt="" className="w-5 h-5 object-contain" />
      );
    case "quickbooks":
      return <SiQuickbooks className="w-5 h-5 text-[#2CA01C]" />;
    default:
      return <Database className="w-5 h-5 text-blue-600" />;
  }
}

export function RevenueConnectionsList({
  connections,
  onAddNew,
  onEdit,
  onDelete,
  canAddNew = true,
  addNewError = null,
  canDelete = () => true,
  onOpenSetupHealthStatus,
  onOpenStepForIssue,
  leftContent,
}: RevenueConnectionsListProps) {
  const [issuesModalConnectionId, setIssuesModalConnectionId] = useState<string | null>(null);
  const issuesModalConnection = issuesModalConnectionId
    ? connections.find((c) => c.id === issuesModalConnectionId)
    : null;

  const getEnvironmentDisplay = (conn: RevenueConnection) => {
    if (conn.type !== "stripe" && conn.type !== "chargebee" && conn.type !== "quickbooks") return "—";
    const sc = (conn.sourceConfig || {}) as Record<string, unknown>;
    const srcEnv =
      conn.sourceEnvironment ??
      (conn.type === "stripe"
        ? (sc.stripeEnvironment as string)
        : conn.type === "chargebee"
          ? (sc.chargebeeEnvironment as string) ?? "Production"
          : (sc.quickbooksEnvironment as string) ?? "");
    return formatEnvName(String(srcEnv || "—"));
  };

  const getAccountIdDisplay = (conn: RevenueConnection) => {
    if (conn.type !== "stripe" && conn.type !== "chargebee" && conn.type !== "quickbooks") return "—";
    const sc = (conn.sourceConfig || {}) as Record<string, unknown>;
    const val =
      conn.accountIdValue ??
      (conn.type === "stripe"
        ? (sc.accountId as string)
        : conn.type === "chargebee"
          ? (sc.subdomain as string)
          : conn.type === "quickbooks"
            ? (sc.realmId as string)
            : "");
    return val || "—";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {leftContent != null ? (
          <div className="ml-2 flex flex-row items-center gap-2">
            <label className="font-medium text-gray-600">Select Environment:</label>
            {leftContent}
          </div>
        ) : null}
        <div className="flex flex-col items-end gap-2 ml-auto">
          {addNewError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {addNewError}
            </div>
          )}
          <button
            onClick={onAddNew}
            disabled={!canAddNew}
            className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors shadow-sm ${
              canAddNew
                ? "bg-primaryColor text-white hover:bg-primaryDarkColor hover:shadow-md"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            <Plus className="w-4 h-4" />
            <span>Connect New Source</span>
          </button>
        </div>
      </div>

      {connections.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
          <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No connections yet
          </h3>
          <p className="text-gray-600 mb-6">
            Connect your first data source to start analyzing your revenue
            metrics in real-time.
          </p>
          <button
            onClick={onAddNew}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primaryColor text-white rounded-lg font-medium hover:bg-primaryDarkColor transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>Get Started</span>
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Environment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Account ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Last Sync
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Error
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {connections.map((conn) => (
                  <tr
                    key={conn.id}
                    className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          {getSourceIcon(conn.type)}
                        </div>
                        <div className="text-sm font-medium text-gray-900 capitalize">
                          {conn.name || conn.type}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      {(() => {
                        const cat = getConnectionCategory(conn.type, conn.sourceCategory);
                        return cat.label === "—"
                          ? <span className="text-sm text-gray-400">—</span>
                          : <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cat.style}`}>{cat.label}</span>;
                      })()}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {conn.isComplete !== false ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-green-600 bg-green-50">
                              Active
                            </span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-amber-700 bg-amber-50">
                              Incomplete
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                      {getEnvironmentDisplay(conn)}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                      {getAccountIdDisplay(conn)}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatLastSyncDate(conn.lastSync)}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm">
                      {(conn.issueCount ?? 0) > 0 ? (
                        <button
                          type="button"
                          onClick={() => setIssuesModalConnectionId(conn.id)}
                          className="flex items-center gap-1.5 text-amber-700 hover:text-amber-800 focus:outline-none underline"
                        >
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          <span>
                            {conn.issueCount} {conn.issueCount === 1 ? "issue" : "issues"} found
                          </span>
                        </button>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => onEdit(conn)}
                          className="p-2 text-gray-500 hover:text-primaryColor hover:bg-blue-50 rounded-lg transition-colors"
                          title="Configure"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {canDelete(conn) && (
                          <button
                            type="button"
                            onClick={() => onDelete(conn)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {issuesModalConnection && issuesModalConnection.issues && issuesModalConnection.issues.length > 0 && (() => {
        const setupStepIds = [2, 3, 4, 5];
        const incompleteSetupSteps = issuesModalConnection.issues.filter((i) => setupStepIds.includes(i.stepId));
        const healthIssues = issuesModalConnection.issues.filter((i) => i.stepId === 7);
        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Issues for {issuesModalConnection.name}
                </h3>
                <button
                  type="button"
                  onClick={() => setIssuesModalConnectionId(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto space-y-6">
                {incompleteSetupSteps.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 tracking-wider mb-2">
                      Incomplete setup steps
                    </h4>
                    <ul className="space-y-2">
                      {incompleteSetupSteps.map((issue) => (
                        <li key={issue.id}>
                          <button
                            type="button"
                            onClick={() => {
                              onOpenStepForIssue?.(issuesModalConnection, issue);
                              setIssuesModalConnectionId(null);
                            }}
                            className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg text-sm text-amber-800 bg-amber-50 hover:bg-amber-100 transition-colors underline"
                          >
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600" />
                            <span className="font-medium">{issue.label}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {healthIssues.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 tracking-wider mb-2">
                      Health issues
                    </h4>
                    <ul className="space-y-2">
                      {healthIssues.map((issue) => (
                        <li key={issue.id}>
                          <button
                            type="button"
                            onClick={() => {
                              onOpenStepForIssue?.(issuesModalConnection, issue);
                              setIssuesModalConnectionId(null);
                            }}
                            className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg text-sm text-amber-800 bg-amber-50 hover:bg-amber-100 transition-colors underline"
                          >
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600" />
                            <span className="font-medium">{issue.label}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <p className="ml-4 text-xs text-gray-600">
                  Click an issue to resolve it in {issuesModalConnection.name} setup.
                </p>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
